import { Injectable } from '@angular/core';
import { LogType } from '@swagger/humanresources';
import {
  FisReportRequest,
  MAX_STORED_REQUESTS,
  REPORT_REQUESTS_STORAGE_KEY,
  StoredReportRequest,
  StoredReportRequests,
} from '@features/users/util-reports';

const OBJECT_TYPE = 'object';
const STRING_TYPE = 'string';

/**
 * Remembers the request behind each started run in localStorage, because the
 * backend keeps only the run's name (#5248). Lost entries (another browser,
 * cleared storage) degrade to "name only, no restart". Every access is
 * guarded: storage can be missing or full.
 */
@Injectable({ providedIn: 'root' })
export class ReportRequestStorage {
  readAll(): StoredReportRequests {
    try {
      const raw = localStorage.getItem(REPORT_REQUESTS_STORAGE_KEY);
      return raw ? asStoredRequests(JSON.parse(raw)) : {};
    } catch {
      return {};
    }
  }

  get(asyncTaskUId: string): StoredReportRequest | undefined {
    return this.readAll()[asyncTaskUId];
  }

  save(asyncTaskUId: string, entry: StoredReportRequest): void {
    this.#write(pruneToNewest({ ...this.readAll(), [asyncTaskUId]: entry }));
  }

  markNotified(asyncTaskUId: string, milestone: LogType): void {
    const entry = this.get(asyncTaskUId);
    if (!entry) return;
    this.save(asyncTaskUId, { ...entry, notifiedMilestone: milestone });
  }

  /** Runs the user has not been told the outcome of yet. */
  unnotifiedUIds(): string[] {
    return Object.entries(this.readAll())
      .filter(([, entry]) => entry.notifiedMilestone === undefined)
      .map(([uId]) => uId);
  }

  #write(entries: StoredReportRequests): void {
    try {
      localStorage.setItem(REPORT_REQUESTS_STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage unavailable or full: the list falls back to the run's name.
    }
  }
}

/** Drops entries of another shape (hand-edited, older version) instead of failing on them (#5248). */
function asStoredRequests(parsed: unknown): StoredReportRequests {
  if (!isRecord(parsed)) return {};
  const valid = Object.entries(parsed).filter((pair): pair is [string, StoredReportRequest] =>
    isStoredRequest(pair[1]),
  );
  return Object.fromEntries(valid);
}

function isStoredRequest(entry: unknown): entry is StoredReportRequest {
  if (!isRecord(entry)) return false;
  const candidate: Partial<StoredReportRequest> = entry;
  return (
    isString(candidate.createdAt) &&
    isStringArray(candidate.branchKeys) &&
    isRequest(candidate.request)
  );
}

function isRequest(request: unknown): request is FisReportRequest {
  if (!isRecord(request)) return false;
  const candidate: Partial<FisReportRequest> = request;
  return (
    isStringArray(candidate.reports) &&
    isStringArray(candidate.branchUIds) &&
    isString(candidate.startDay) &&
    isString(candidate.stopDay)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === OBJECT_TYPE && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(isString);
}

function isString(value: unknown): value is string {
  return typeof value === STRING_TYPE;
}

function pruneToNewest(entries: StoredReportRequests): StoredReportRequests {
  const newestFirst = Object.entries(entries).sort(([, a], [, b]) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  return Object.fromEntries(newestFirst.slice(0, MAX_STORED_REQUESTS));
}
